<template>
  <Teleport to="body">
    <WizardModalModern
      v-if="isVisible"
      :show-modal="isVisible"
      :modal-title="modalTitle"
      :steps="(wizardSteps as any)"
      :current-step="currentWizardStep"
      elem-id="pdv-servico-wizard"
      :allow-step-click="true"
      :validate-fn="validateStep"
      @close="handleClose"
      @cancel="handleClose"
      @finish="handleFinish"
      @next="handleWizardNext"
      @back="handleWizardBack"
      @step-click="handleWizardStepClick"
    >
      <template v-for="(step, idx) in wizardSteps" :key="step.key" #[`step-${idx}`]>
        <!-- Step: Category -->
        <template v-if="step.key === 'category'">
          <h3 class="step-title">{{ t('Categoria') }}</h3>
          <p class="step-desc">{{ t('Selecione a categoria do serviço') }}</p>
          <div v-if="catalogsLoading" class="loading-indicator">
            <i class="pi pi-spinner pi-spin"></i> {{ t('Carregando categorias...') }}
          </div>
          <div v-else>
            <div class="selection-search">
              <i class="pi pi-search selection-search-icon"></i>
              <input
                type="text"
                v-model="categorySearchQuery"
                :placeholder="t('Procurar categoria...')"
                class="selection-search-input"
                data-testid="pdv-category-search-input"
              />
            </div>
            <div class="selection-grid">
              <div
                v-for="cat in filteredCategories"
                :key="cat.ID"
                class="selection-card"
                :class="{ 'selection-card--selected': selectedCategoryIdStr === String(cat.ID) }"
                :data-testid="`service-category-${cat.ID}`"
                @click="selectedCategoryIdStr = String(cat.ID)"
                tabindex="0"
                role="radio"
                :aria-checked="selectedCategoryIdStr === String(cat.ID)"
              >
                <div class="selection-left">
                  <div class="selection-indicator" :class="{ 'selection-indicator--active': selectedCategoryIdStr === String(cat.ID) }"></div>
                  <span v-if="cat.icon" class="category-icon" v-html="sanitizeSvgIcon(cat.icon)"></span>
                  <div class="selection-content">
                    <div class="selection-title">{{ cat.Name }}</div>
                    <div class="selection-subtitle">{{ categoryServiceLabel(cat.ID) }}</div>
                  </div>
                </div>
                <i class="pi pi-chevron-right selection-chevron"></i>
              </div>
            </div>
          </div>
        </template>

        <!-- Step: Attribute (dynamic) -->
        <template v-else-if="step.key.startsWith('attr-') && getAttrForStep(step.key)">
          <h3 class="step-title">{{ getAttrForStep(step.key).so_item_attribute_name }}</h3>
          <p class="step-desc">{{ t('Preencha as informações abaixo') }}</p>
          <div class="step-input-area">
            <input-text
              v-if="getAttrForStep(step.key).so_item_attribute_type === 'text'"
              :elem-id="'attr-' + getAttrForStep(step.key).so_category_attribute_attribute_id"
              :data-testid="'pdv-attr-text-' + getAttrForStep(step.key).so_category_attribute_attribute_id"
              :label="getAttrForStep(step.key).so_item_attribute_name"
              :modelValue="formData.attributeValues[getAttrForStep(step.key).so_category_attribute_attribute_id] || ''"
              @update:modelValue="setAttributeValue(getAttrForStep(step.key).so_category_attribute_attribute_id, $event ?? '')"
              :required="getAttrForStep(step.key).so_category_attribute_required == 1"
            />
            <input-number
              v-else-if="getAttrForStep(step.key).so_item_attribute_type === 'number'"
              :elem-id="'attr-' + getAttrForStep(step.key).so_category_attribute_attribute_id"
              :data-testid="'pdv-attr-number-' + getAttrForStep(step.key).so_category_attribute_attribute_id"
              :label="getAttrForStep(step.key).so_item_attribute_name"
              :modelValue="formData.attributeValues[getAttrForStep(step.key).so_category_attribute_attribute_id] ? Number(formData.attributeValues[getAttrForStep(step.key).so_category_attribute_attribute_id]) : null"
              @update:modelValue="setAttributeValue(getAttrForStep(step.key).so_category_attribute_attribute_id, $event != null ? String($event) : '')"
              :required="getAttrForStep(step.key).so_category_attribute_required == 1"
            />
            <div v-else-if="getAttrForStep(step.key).so_item_attribute_type === 'boolean'" class="selection-grid selection-grid-compact">
              <div
                v-for="opt in booleanOptions"
                :key="opt.ID"
                class="selection-card"
                :class="{ 'selection-card--selected': formData.attributeValues[getAttrForStep(step.key).so_category_attribute_attribute_id] === opt.ID }"
                :data-testid="`pdv-attr-boolean-${getAttrForStep(step.key).so_category_attribute_attribute_id}-${opt.ID}`"
                @click="setAttributeValue(getAttrForStep(step.key).so_category_attribute_attribute_id, opt.ID)"
                tabindex="0"
                role="radio"
                :aria-checked="formData.attributeValues[getAttrForStep(step.key).so_category_attribute_attribute_id] === opt.ID"
              >
                <div class="selection-left">
                  <div class="selection-indicator" :class="{ 'selection-indicator--active': formData.attributeValues[getAttrForStep(step.key).so_category_attribute_attribute_id] === opt.ID }"></div>
                  <div class="selection-content">
                    <div class="selection-title">{{ opt.Name }}</div>
                  </div>
                </div>
                <i class="pi pi-chevron-right selection-chevron"></i>
              </div>
            </div>
            <div v-else-if="getAttrForStep(step.key).so_item_attribute_type === 'select' && (getAttrForStep(step.key).options?.length ?? 0) > 0" class="selection-grid selection-grid-compact">
              <div
                v-for="opt in (getAttrForStep(step.key).options ?? [])"
                :key="opt.so_item_attribute_option_value"
                class="selection-card"
                :class="{ 'selection-card--selected': formData.attributeValues[getAttrForStep(step.key).so_category_attribute_attribute_id] === opt.so_item_attribute_option_value }"
                :data-testid="`pdv-attr-select-${getAttrForStep(step.key).so_category_attribute_attribute_id}-${opt.so_item_attribute_option_value}`"
                @click="setAttributeValue(getAttrForStep(step.key).so_category_attribute_attribute_id, opt.so_item_attribute_option_value ?? '')"
                tabindex="0"
                role="radio"
                :aria-checked="formData.attributeValues[getAttrForStep(step.key).so_category_attribute_attribute_id] === opt.so_item_attribute_option_value"
              >
                <div class="selection-left">
                  <div class="selection-indicator" :class="{ 'selection-indicator--active': formData.attributeValues[getAttrForStep(step.key).so_category_attribute_attribute_id] === opt.so_item_attribute_option_value }"></div>
                  <span v-if="opt.so_item_attribute_option_icon_svg" class="category-icon" v-html="sanitizeSvgIcon(opt.so_item_attribute_option_icon_svg)"></span>
                  <div class="selection-content">
                    <div class="selection-title">{{ opt.so_item_attribute_option_value }}</div>
                  </div>
                </div>
                <i class="pi pi-chevron-right selection-chevron"></i>
              </div>
            </div>
            <input-text-area
              v-else-if="getAttrForStep(step.key).so_item_attribute_type === 'textarea'"
              :elem-id="'attr-textarea-' + getAttrForStep(step.key).so_category_attribute_attribute_id"
              :data-testid="'pdv-attr-textarea-' + getAttrForStep(step.key).so_category_attribute_attribute_id"
              :label="getAttrForStep(step.key).so_item_attribute_name"
              :modelValue="formData.attributeValues[getAttrForStep(step.key).so_category_attribute_attribute_id] || ''"
              @update:modelValue="setAttributeValue(getAttrForStep(step.key).so_category_attribute_attribute_id, $event ?? '')"
              :required="getAttrForStep(step.key).so_category_attribute_required == 1"
            />
            <!-- photo on a CATEGORY attribute: the shared catalog now allows photo-typed defs to be
                 bound to categories. Until a category photo-upload flow exists, accept a URL via a
                 text input so the step is fillable and never soft-locks isAttributeStepValid. -->
            <input-text
              v-else-if="getAttrForStep(step.key).so_item_attribute_type === 'photo'"
              :elem-id="'attr-photo-' + getAttrForStep(step.key).so_category_attribute_attribute_id"
              :data-testid="'pdv-attr-photo-' + getAttrForStep(step.key).so_category_attribute_attribute_id"
              :label="getAttrForStep(step.key).so_item_attribute_name"
              :placeholder="t('URL da foto')"
              :modelValue="formData.attributeValues[getAttrForStep(step.key).so_category_attribute_attribute_id] || ''"
              @update:modelValue="setAttributeValue(getAttrForStep(step.key).so_category_attribute_attribute_id, $event ?? '')"
              :required="getAttrForStep(step.key).so_category_attribute_required == 1"
            />
          </div>
        </template>

        <!-- Step: Customer Requests -->
        <template v-else-if="step.key === 'customer_requests'">
          <h3 class="step-title">{{ t('Solicitações do Cliente') }}</h3>
          <p class="step-desc">{{ t('Registre as solicitações feitas pelo cliente') }}</p>
          <SOOrderItemRequestsEditor v-model="formData.requests" data-testid="pdv-customer-requests-editor" />
        </template>

        <!-- Step: Plan -->
        <template v-else-if="step.key === 'plan'">
          <h3 class="step-title">{{ t('Plano de Serviço') }}</h3>
          <p class="step-desc">{{ t('Escolha o plano desejado') }}</p>
          <div v-if="servicePrioritiesLoading" class="loading-indicator">
            <i class="pi pi-spinner pi-spin"></i> {{ t('Carregando planos...') }}
          </div>
          <div v-else>
            <div class="selection-grid">
              <div
                v-for="plan in filteredServicePriorityOptions"
                :key="plan.ID"
                class="selection-card"
                :class="{ 'selection-card--selected': selectedPlanId === plan.ID, 'plan-card--colored': !!plan.serviceColor || !!plan.priorityColor }"
                :style="{ '--plan-bg': plan.serviceColor || 'transparent', '--plan-border': plan.priorityColor || plan.serviceColor || 'var(--p-surface-200)', '--plan-text': plan.textColor }"
                :data-testid="`service-plan-${plan.ID}`"
                @click="selectedPlanId = plan.ID"
                tabindex="0"
                role="radio"
                :aria-checked="selectedPlanId === plan.ID"
              >
                <div class="selection-left">
                  <div class="selection-indicator" :class="{ 'selection-indicator--active': selectedPlanId === plan.ID }"></div>
                  <div class="selection-content">
                    <div class="selection-title">{{ plan.Name }}</div>
                    <div class="selection-subtitle">{{ plan.description }}</div>
                  </div>
                </div>
                <i class="pi pi-chevron-right selection-chevron"></i>
              </div>
            </div>
            <div class="deadline-display" v-if="formData.selectedPlan">
              <i class="pi pi-clock"></i>
              {{ t('Prazo estimado') }}: {{ formData.selectedPlan.so_service_priority_deadline_days }} {{ t('dias úteis') }}
            </div>
            <!-- Só as compras que cobrem ESTE plano (serviço + prioridade), nesta
                 unidade, com saldo e na validade. A trava de verdade é do backend. -->
            <div v-if="comboOptions.length > 1" class="combo-selector" data-testid="pdv-combo-selector">
              <select-list
                elem-id="pdv-combo-compra"
                data-testid="pdv-combo-compra-select"
                :label="t('Usar combo do cliente')"
                :placeholder="t('Não usar combo')"
                :itens-select="comboOptions"
                :auto-select-first="false"
                v-model="comboSelectionId"
              />
              <small class="combo-hint">{{ t('O par sai a 0 e reserva 1 do saldo; só é consumido na entrega.') }}</small>
            </div>
          </div>
        </template>

        <!-- Step: QR Code (depois do Plano de Serviço pois as tags dependem do plano escolhido) -->
        <template v-else-if="step.key === 'qrcode'">
          <h3 class="step-title">{{ t('QR Code') }}</h3>
          <p class="step-desc">{{ t('Escaneie ou digite o código do item') }}</p>
          <div class="qrcode-input-wrapper">
            <input-qrcode
              elem-id="pdv-qrcode"
              data-testid="pdv-qrcode-input"
              :label="t('QR Code')"
              v-model="formData.qrcode"
              :required="true"
              :placeholder="t('Digite ou escaneie o QR Code')"
            />
            <div
              v-if="qrCheck.status.value === 'checking'"
              class="qrcode-check-status qrcode-check-checking"
              data-testid="pdv-qrcode-status-checking"
            >
              <i class="pi pi-spinner pi-spin"></i>
              {{ t('Verificando disponibilidade do QR Code…') }}
            </div>
            <div
              v-else-if="qrCheck.status.value === 'unique'"
              class="qrcode-check-status qrcode-check-unique"
              data-testid="pdv-qrcode-status-unique"
            >
              <i class="pi pi-check"></i>
              {{ t('QR Code disponível') }}
            </div>
            <div
              v-else-if="qrCheck.status.value === 'duplicate'"
              class="qrcode-check-status qrcode-check-duplicate"
              data-testid="pdv-qrcode-status-duplicate"
            >
              <i class="pi pi-times"></i>
              <template v-if="qrCheck.holder.value">
                {{ t('QR Code já está em uso na OS') }} {{ qrCheck.holder.value.orderId }}
                ({{ qrCheck.holder.value.clientName || '-' }}, {{ getOrderStatus(qrCheck.holder.value.status).label }})
              </template>
              <template v-else>{{ t('QR Code já está em uso') }}</template>
            </div>
            <div
              v-else-if="qrCheck.status.value === 'error'"
              class="qrcode-check-status qrcode-check-error"
              data-testid="pdv-qrcode-status-error"
            >
              <i class="pi pi-exclamation-triangle"></i>
              {{ t('Não foi possível verificar o QR Code') }}
            </div>
          </div>
        </template>

        <!-- Step: Additional Services -->
        <template v-else-if="step.key === 'additional-services'">
          <h3 class="step-title">{{ t('Serviços Adicionais') }}</h3>
          <p class="step-desc">{{ t('Adicione serviços extras se necessário') }}</p>
          <div v-if="additionalServicesLoading" class="loading-indicator">
            <i class="pi pi-spinner pi-spin"></i> {{ t('Carregando...') }}
          </div>
          <div v-else>
            <div class="selection-grid">
              <div
                v-for="svc in additionalServicesOptions"
                :key="svc.ID"
                class="selection-card"
                :class="{ 'selection-card--selected': selectedAdditionalServiceIds.includes(svc.ID) }"
                :data-testid="`additional-services-${svc.ID}`"
                @click="toggleAdditionalService(svc.ID)"
                tabindex="0"
                role="checkbox"
                :aria-checked="selectedAdditionalServiceIds.includes(svc.ID)"
              >
                <div class="selection-left">
                  <div class="selection-checkbox" :class="{ 'selection-checkbox--active': selectedAdditionalServiceIds.includes(svc.ID) }">
                    <i v-if="selectedAdditionalServiceIds.includes(svc.ID)" class="pi pi-check"></i>
                  </div>
                  <div class="selection-content">
                    <div class="selection-title">{{ svc.Name }}</div>
                    <div class="selection-subtitle">{{ svc.description }}</div>
                  </div>
                </div>
                <i class="pi pi-chevron-right selection-chevron"></i>
              </div>
            </div>
            <!-- Inline per-service attribute sub-panel: for each selected additional
                 service that has bound attributes, render an input per attribute type. -->
            <div
              v-for="svc in formData.selectedAdditionalServices"
              :key="'as-attr-panel-' + svc.so_additional_service_id"
            >
              <div
                v-if="attributesForService(svc.so_additional_service_id ?? '').length > 0"
                class="additional-service-attr-panel"
                :data-testid="`pdv-as-attr-panel-${svc.so_additional_service_id}`"
              >
                <h5>{{ svc.so_additional_service_name }}</h5>
                <div
                  v-for="attr in attributesForService(svc.so_additional_service_id ?? '')"
                  :key="'as-attr-' + svc.so_additional_service_id + '-' + attr.so_asal_attribute_id"
                  class="additional-service-attr-item"
                >
                  <input-text
                    v-if="attr.so_item_attribute_type === 'text'"
                    :elem-id="'as-attr-text-' + svc.so_additional_service_id + '-' + attr.so_asal_attribute_id"
                    :data-testid="`pdv-as-attr-text-${svc.so_additional_service_id}-${attr.so_asal_attribute_id}`"
                    :label="attr.so_item_attribute_name"
                    :required="isAdditionalServiceAttrRequired(attr)"
                    :model-value="getAdditionalServiceAttrValue(svc.so_additional_service_id ?? '', attr.so_asal_attribute_id)"
                    @update:model-value="setAdditionalServiceAttrValue(svc.so_additional_service_id ?? '', attr.so_asal_attribute_id, $event ?? '')"
                  />
                  <input-text-area
                    v-else-if="attr.so_item_attribute_type === 'textarea'"
                    :elem-id="'as-attr-textarea-' + svc.so_additional_service_id + '-' + attr.so_asal_attribute_id"
                    :data-testid="`pdv-as-attr-textarea-${svc.so_additional_service_id}-${attr.so_asal_attribute_id}`"
                    :label="attr.so_item_attribute_name"
                    :required="isAdditionalServiceAttrRequired(attr)"
                    :model-value="getAdditionalServiceAttrValue(svc.so_additional_service_id ?? '', attr.so_asal_attribute_id)"
                    @update:model-value="setAdditionalServiceAttrValue(svc.so_additional_service_id ?? '', attr.so_asal_attribute_id, $event ?? '')"
                  />
                  <input-number
                    v-else-if="attr.so_item_attribute_type === 'number'"
                    :elem-id="'as-attr-number-' + svc.so_additional_service_id + '-' + attr.so_asal_attribute_id"
                    :data-testid="`pdv-as-attr-number-${svc.so_additional_service_id}-${attr.so_asal_attribute_id}`"
                    :label="attr.so_item_attribute_name"
                    :required="isAdditionalServiceAttrRequired(attr)"
                    :model-value="getAdditionalServiceAttrValue(svc.so_additional_service_id ?? '', attr.so_asal_attribute_id) ? Number(getAdditionalServiceAttrValue(svc.so_additional_service_id ?? '', attr.so_asal_attribute_id)) : null"
                    @update:model-value="setAdditionalServiceAttrValue(svc.so_additional_service_id ?? '', attr.so_asal_attribute_id, $event != null ? String($event) : '')"
                  />
                  <buttons-grid
                    v-else-if="attr.so_item_attribute_type === 'boolean'"
                    :label="attr.so_item_attribute_name"
                    :itensSelect="booleanOptions"
                    :modelValue="getAdditionalServiceAttrValue(svc.so_additional_service_id ?? '', attr.so_asal_attribute_id) || null"
                    @update:modelValue="setAdditionalServiceAttrValue(svc.so_additional_service_id ?? '', attr.so_asal_attribute_id, $event ?? '')"
                    :elemId="'as-attr-boolean-' + svc.so_additional_service_id + '-' + attr.so_asal_attribute_id"
                    :data-testid="`pdv-as-attr-boolean-${svc.so_additional_service_id}-${attr.so_asal_attribute_id}`"
                    size="md"
                  />
                  <buttons-grid
                    v-else-if="attr.so_item_attribute_type === 'select'"
                    :label="attr.so_item_attribute_name"
                    :itensSelect="(attr.options ?? []).map(o => ({ ID: o.so_item_attribute_option_value, Name: o.so_item_attribute_option_value, icon: o.so_item_attribute_option_icon_svg || null, severity: 'primary' }))"
                    :modelValue="getAdditionalServiceAttrValue(svc.so_additional_service_id ?? '', attr.so_asal_attribute_id) || null"
                    @update:modelValue="setAdditionalServiceAttrValue(svc.so_additional_service_id ?? '', attr.so_asal_attribute_id, $event ?? '')"
                    :elemId="'as-attr-select-' + svc.so_additional_service_id + '-' + attr.so_asal_attribute_id"
                    :data-testid="`pdv-as-attr-select-${svc.so_additional_service_id}-${attr.so_asal_attribute_id}`"
                    size="md"
                    :searchEnabled="true"
                  />
                  <div v-else-if="attr.so_item_attribute_type === 'photo'" class="additional-service-photo">
                    <label>
                      <span v-if="isAdditionalServiceAttrRequired(attr)" class="required-indicator">*</span>
                      {{ attr.so_item_attribute_name }}
                    </label>
                    <div v-if="getAdditionalServiceAttrValue(svc.so_additional_service_id ?? '', attr.so_asal_attribute_id)" class="photo-preview">
                      <img :src="getAdditionalServiceAttrValue(svc.so_additional_service_id ?? '', attr.so_asal_attribute_id)" :alt="attr.so_item_attribute_name" class="preview-image" />
                    </div>
                    <input
                      :data-testid="`pdv-as-attr-photo-${svc.so_additional_service_id}-${attr.so_asal_attribute_id}`"
                      type="file"
                      accept="image/*"
                      class="file-input"
                      @change="(e) => handleAdditionalServicePhotoChange(svc.so_additional_service_id ?? '', attr, e)"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div class="services-total" v-if="additionalServicesTotal > 0">
              {{ t('Total de serviços') }}: {{ n(additionalServicesTotal, 'currency') }}
            </div>
          </div>
        </template>

        <!-- Step: Products -->
        <template v-else-if="step.key === 'products'">
          <h3 class="step-title">{{ t('Produtos') }}</h3>
          <p class="step-desc">{{ t('Adicione produtos ao serviço') }}</p>
          <div class="products-step-wrapper">
            <SOProductSelector
              v-model="formData.selectedProducts"
              :products="productsList"
              mode="modal"
              :show-total="true"
              :franchiseId="franchiseId"
            />
          </div>
        </template>

        <!-- Step: Summary -->
        <template v-else-if="step.key === 'summary'">
          <h3 class="step-title">{{ t('Resumo') }}</h3>
          <p class="step-desc">{{ t('Confira os dados antes de finalizar') }}</p>
          <div class="item-summary">
            <div class="summary-section-title">{{ t('Atributos') }}</div>
            <div class="summary-row" v-for="attr in categoryAttributes" :key="'summary-attr-' + attr.so_category_attribute_attribute_id">
              <span class="summary-label">{{ attr.so_item_attribute_name }}</span>
              <span class="summary-value">{{ formatAttributeSummaryValue(attr, formData.attributeValues[attr.so_category_attribute_attribute_id]) }}</span>
            </div>

            <div class="summary-section-title">{{ t('Valores') }}</div>
            <div class="summary-row">
              <span class="summary-label">{{ t('Plano') }}: {{ formData.selectedPlan?.so_service_name || '-' }} - {{ formData.selectedPlan?.so_priority_name || '-' }}</span>
              <span class="summary-value" data-testid="pdv-summary-plan-price">{{ n(planPrice, 'currency') }}</span>
            </div>
            <div class="summary-row" v-if="comboPurchaseId" data-testid="pdv-summary-combo">
              <span class="summary-label">{{ t('Coberto pelo combo') }}</span>
              <span class="summary-value">{{ comboSelecionadoNome }}</span>
            </div>
            <div class="summary-row" v-if="additionalServicesTotal > 0">
              <span class="summary-label">{{ t('Serviços adicionais') }}</span>
              <span class="summary-value">{{ n(additionalServicesTotal, 'currency') }}</span>
            </div>
            <div class="summary-row" v-if="productsTotal > 0">
              <span class="summary-label">{{ t('Produtos') }}</span>
              <span class="summary-value">{{ n(productsTotal, 'currency') }}</span>
            </div>
            <div class="summary-total-row">
              <span>{{ t('Total do item') }}</span>
              <span class="summary-value">{{ n(itemTotal, 'currency') }}</span>
            </div>
            <div class="summary-deadline-row">
              <span>{{ t('Prazo estimado') }}</span>
              <span class="summary-value">{{ estimatedDays }} {{ t('dias úteis') }}</span>
            </div>
          </div>
        </template>
      </template>
    </WizardModalModern>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { sanitizeSvgIcon } from '../../../utils/sanitizeSvgIcon';
import WizardModalModern from '../../../ui/WizardModalModern.vue';
import { eventBus } from '../../../eventBus';
import { useSOCheckout } from '../../../composables/useSOCheckout';
import { useQrcodeUniquenessCheck } from '../../../composables/useQrcodeUniquenessCheck';
import { useSOStatusMap } from '../../../composables/useSOStatusMap';
import { useSOServicePriorities } from '../../../composables/useSOServicePriorities';
import { useSOAdditionalServices } from '../../../composables/useSOAdditionalServices';
import { useSOAdditionalServiceAttributes } from '../../../composables/useSOAdditionalServiceAttributes';
import { useSOCatalog } from '../../../composables/useSOCatalog';
import { useSOItemAttributes } from '../../../composables/useSOItemAttributes';
import { useSOLaudosComparison } from '../../../composables/useSOLaudosComparison';
import { useSOOrderItemRequests } from '../../../composables/useSOOrderItemRequests';
import { useUserPermissions } from '../../../composables/usePermissions';
import { useClienteCombos, comprasCompativeis, type CompraCombo } from '../../../composables/useClienteCombos';
import { config } from '../../../services/configService';
import { log, error as logError } from '../../../utils/DebugLogger';

type Id = number | string;

interface ServicePriority {
  so_service_priority_id?: Id;
  so_service_priority_service_id?: Id;
  so_service_priority_priority_id?: Id;
  so_service_priority_price?: number;
  so_service_priority_deadline_days?: number | string;
  so_service_category_id?: Id;
  so_service_category_name?: string;
  so_service_category_icon_svg?: string | null;
  so_service_name?: string;
  so_priority_name?: string;
  so_service_priority_order?: number | string;
  so_service_color?: string | null;
  so_priority_color?: string | null;
  service_id?: Id;
  priority_id?: Id;
  [key: string]: unknown;
}

interface AdditionalService {
  so_additional_service_id?: Id;
  so_additional_service_name?: string;
  so_additional_service_default_price?: number;
  so_additional_service_additional_deadline_days?: number | string;
  [key: string]: unknown;
}

interface ProductItem {
  so_product_id?: Id;
  so_product_default_price?: number;
  [key: string]: unknown;
}

interface CategoryAttributeOption {
  so_item_attribute_option_value?: string;
  so_item_attribute_option_icon_svg?: string | null;
  [key: string]: unknown;
}

interface AdditionalServiceAttributeOption {
  so_item_attribute_option_value: string;
  so_item_attribute_option_icon_svg?: string | null;
  [key: string]: unknown;
}

interface AdditionalServiceAttributeBinding {
  so_asal_attribute_id: Id;
  so_asal_required: number | string;
  so_asal_display_order?: number;
  so_item_attribute_name: string;
  so_item_attribute_type: string;
  options?: AdditionalServiceAttributeOption[];
  [key: string]: unknown;
}

interface CategoryAttribute {
  so_category_attribute_attribute_id: Id;
  so_category_attribute_required?: number | string;
  so_item_attribute_type?: string;
  so_item_attribute_name?: string;
  options?: CategoryAttributeOption[];
  [key: string]: unknown;
}

interface RequestItem {
  description?: string;
  photo?: string | null;
  photo_file?: File | null;
  display_order?: number;
  [key: string]: unknown;
}

interface OrderItemAttribute {
  so_oiav_attribute_id?: Id;
  so_oiav_value?: string;
}

interface EditItem {
  so_order_item_id?: Id;
  so_order_item_qrcode?: string;
  so_order_item_service_id?: Id;
  so_order_item_priority_id?: Id;
  so_order_item_combo_purchase_id?: number | null;
  attributes?: OrderItemAttribute[];
  [key: string]: unknown;
}

interface ChildItem {
  so_order_item_type?: string;
  so_order_item_additional_service_id?: Id;
  so_order_item_product_id?: Id;
  so_order_item_quantity?: number;
  so_order_item_description?: string;
  so_order_item_unit_price?: number;
  [key: string]: unknown;
}

interface OpenPayload {
  orderId?: Id;
  franchiseId?: Id;
  editItem?: EditItem | null;
  children?: ChildItem[];
  [key: string]: unknown;
}

interface FormData {
  categoryId: number | null;
  qrcode: string;
  attributeValues: Record<string, string>;
  planId: Id | null;
  selectedPlan: ServicePriority | null;
  selectedAdditionalServices: AdditionalService[];
  selectedProducts: Record<string, number>;
  requests: RequestItem[];
}

const { t, n } = useI18n();
const { addItem, updateItem, getOrder } = useSOCheckout();
const qrCheck = useQrcodeUniquenessCheck();
const { getOrderStatus } = useSOStatusMap();
const { fetchByFranchise: fetchServicePrioritiesByFranchise, isLoading: servicePrioritiesLoading } = useSOServicePriorities();
const { fetchByCategory: fetchAdditionalServicesByCategory, isLoading: additionalServicesLoading } = useSOAdditionalServices();
const { fetchServiceAttributes: fetchAdditionalServiceAttrs } = useSOAdditionalServiceAttributes();
const productsCatalog = useSOCatalog('products');
const { fetchCategoryAttributes: fetchCategoryAttrs } = useSOItemAttributes();
const { uploadAttributePhoto } = useSOLaudosComparison();
const { saveAll, uploadPhoto } = useSOOrderItemRequests();
const { canUseCombos } = useUserPermissions();
const { fetchCompras, vincular, desvincular } = useClienteCombos();

// Combo pré-pago (NS 2026-0090): feature ligada e grupo de balcão (Operador fora).
const combosEnabled = config.isFeatureEnabled('combos');
const comprasDoCliente = ref<CompraCombo[]>([]);
// A compra escolhida no wizard; na edição, `comboOriginalId` é a que o item já tinha.
const comboPurchaseId = ref<number | null>(null);
const comboOriginalId = ref<number | null>(null);

const isVisible = ref<boolean>(false);
const orderId = ref<Id | null>(null);
const franchiseId = ref<number | null>(null);
const editItemId = ref<Id | null>(null);
const servicePriorities = ref<ServicePriority[]>([]);
const additionalServicesList = ref<AdditionalService[]>([]);
const productsList = ref<ProductItem[]>([]);
const categoryAttributes = ref<CategoryAttribute[]>([]);

// Per-additional-service attribute bindings, keyed by additional_service_id.
const additionalServiceAttributes = ref<Record<string, AdditionalServiceAttributeBinding[]>>({});
// Submitted attribute values, keyed by `${additional_service_id}:${attribute_id}` so
// multiple selected services never collide on a shared attribute_id.
const additionalServiceAttributeValues = reactive<Record<string, string>>({});
// Pending photo File objects keyed the same way — uploaded in phase 2 (after add_item
// returns the child order_item_id), since upload_attribute_photo needs an existing id.
const additionalServiceAttributePhotos = reactive<Record<string, File | null>>({});

const isEditMode = computed<boolean>(() => !!editItemId.value);

const modalTitle = computed<string>(() =>
  isEditMode.value ? t('Editar Serviço') : t('Adicionar Serviço à Ordem')
);


const catalogsLoading = ref<boolean>(false);
const categorySearchQuery = ref('');

const formData = reactive<FormData>({
  categoryId: null,
  qrcode: '',
  attributeValues: {},
  planId: null,
  selectedPlan: null,
  selectedAdditionalServices: [],
  selectedProducts: {},
  requests: []
});

const currentWizardStep = ref<number>(0);

// Live QR-code uniqueness check — both camera scans and manual typing funnel
// through the single v-model on <input-qrcode>, so one watch covers both paths
// (and the edit-prefill at handleModalOpen). Self-exclusion via editItemId so
// the item's own saved QR is treated as available on edit.
// orderId scopes the check to the order's franchise (the wizard is always opened
// for one), so it no longer depends on what the global filter happens to select.
watch(() => formData.qrcode, (qr) => {
  qrCheck.check(qr, editItemId.value, orderId.value);
});

const validateStep = (stepIndex: number): boolean => {
  const step = wizardSteps.value[stepIndex];
  return step?.validate?.() ?? true;
};

const handleWizardNext = (): void => {
  if (currentWizardStep.value < wizardSteps.value.length - 1) {
    currentWizardStep.value++;
  }
};

const handleWizardBack = (): void => {
  if (currentWizardStep.value > 0) {
    currentWizardStep.value--;
  }
};

const handleWizardStepClick = (stepIndex: number): void => {
  if (stepIndex < currentWizardStep.value) {
    currentWizardStep.value = stepIndex;
  }
};

const booleanOptions = [
  { ID: '1', Name: t('Sim'), severity: 'success' },
  { ID: '0', Name: t('Não'), severity: 'secondary' }
];

const isRequiredAttribute = (attr?: CategoryAttribute): boolean => attr?.so_category_attribute_required == 1;

const hasAttributeValue = (attr?: CategoryAttribute): boolean => {
  const key = String(attr?.so_category_attribute_attribute_id ?? '');
  const value = formData.attributeValues[key];
  return value !== '' && value !== null && value !== undefined;
};

const isAttributeStepValid = (attr?: CategoryAttribute): boolean => !isRequiredAttribute(attr) || hasAttributeValue(attr);

const getAttrForStep = (stepKey: string): CategoryAttribute => {
  const attrId = stepKey.replace('attr-', '');
  const found = categoryAttributes.value.find((a: CategoryAttribute) => String(a.so_category_attribute_attribute_id) === attrId);
  // Template guards via v-if before access; return empty shape when not found
  return found || ({ so_category_attribute_attribute_id: 0, options: [] } as CategoryAttribute);
};

const wizardSteps = computed(() => [
  { key: 'category', title: t('Categoria'), validate: () => !!formData.categoryId },
  ...categoryAttributes.value.map(attr => ({
    key: `attr-${attr.so_category_attribute_attribute_id}`,
    title: attr.so_item_attribute_name,
    validate: () => isAttributeStepValid(attr)
  })),
  {
    key: 'customer_requests',
    title: t('Solicitações do Cliente'),
    validate: () => (formData.requests || []).every(r => !!(r.description && r.description.trim())),
  },
  { key: 'plan', title: t('Plano de Serviço'), validate: () => !!formData.planId },
  { key: 'qrcode', title: t('QR Code'), validate: () => !!formData.qrcode && qrCheck.isUnique.value },
  { key: 'additional-services', title: t('Serviços Adicionais'), validate: () => additionalServiceAttributesValid.value },
  { key: 'products', title: t('Produtos Adicionais'), validate: () => true },
  { key: 'summary', title: t('Resumo'), validate: () => true }
]);

const formatAttributeSummaryValue = (attr: CategoryAttribute, value: unknown): string => {
  if (value === '' || value === null || value === undefined) {
    return '-';
  }

  if (attr.so_item_attribute_type === 'boolean') {
    if (value === '1' || value === 1 || value === true) {
      return t('Sim');
    }

    if (value === '0' || value === 0 || value === false) {
      return t('Não');
    }
  }

  return String(value);
};

const categoryOptions = computed(() => {
  const seen = new Map();
  for (const sp of servicePriorities.value) {
    const catId = String(sp.so_service_category_id);
    if (!seen.has(catId)) {
      seen.set(catId, {
        ID: catId,
        Name: sp.so_service_category_name,
        icon: sp.so_service_category_icon_svg || null
      });
    }
  }
  return Array.from(seen.values());
});

const filteredCategories = computed(() => {
  if (!categorySearchQuery.value) return categoryOptions.value;
  const q = categorySearchQuery.value.toLowerCase();
  return categoryOptions.value.filter(cat =>
    String(cat.Name).toLowerCase().includes(q)
  );
});

const categoryServiceLabel = (catId: string | number): string => {
  const count = servicePriorities.value.filter(sp => String(sp.so_service_category_id) === String(catId)).length;
  return `${count} ${count === 1 ? t('serviço disponível') : t('serviços disponíveis')}`;
};

const setAttributeValue = (attributeId: Id, value: string): void => {
  formData.attributeValues = { ...formData.attributeValues, [String(attributeId)]: value };
};

// --- Additional-service attribute value helpers (keyed per service+attribute) ---
const asAttrKey = (serviceId: Id, attributeId: Id): string => `${serviceId}:${attributeId}`;

const isAdditionalServiceAttrRequired = (attr: AdditionalServiceAttributeBinding): boolean =>
  attr.so_asal_required == 1;

const getAdditionalServiceAttrValue = (serviceId: Id, attributeId: Id): string =>
  additionalServiceAttributeValues[asAttrKey(serviceId, attributeId)] || '';

const setAdditionalServiceAttrValue = (serviceId: Id, attributeId: Id, value: string): void => {
  additionalServiceAttributeValues[asAttrKey(serviceId, attributeId)] = value ?? '';
};

const attributesForService = (serviceId: Id): AdditionalServiceAttributeBinding[] =>
  additionalServiceAttributes.value[String(serviceId)] || [];

const handleAdditionalServicePhotoChange = (serviceId: Id, attr: AdditionalServiceAttributeBinding, event: Event): void => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (!file) return;
  const key = asAttrKey(serviceId, attr.so_asal_attribute_id);
  additionalServiceAttributePhotos[key] = file;
  // Local preview URL stands in until phase-2 upload swaps it for the blob URL.
  setAdditionalServiceAttrValue(serviceId, attr.so_asal_attribute_id, URL.createObjectURL(file));
};

// True once every required attribute of every selected additional service has a value
// (a chosen photo File counts — its URL is produced during the two-phase upload on finish).
const additionalServiceAttributesValid = computed<boolean>(() =>
  formData.selectedAdditionalServices.every((svc) => {
    const serviceId = svc.so_additional_service_id;
    if (serviceId === undefined) return true;
    return attributesForService(serviceId).every((attr) => {
      if (!isAdditionalServiceAttrRequired(attr)) return true;
      const key = asAttrKey(serviceId, attr.so_asal_attribute_id);
      if (attr.so_item_attribute_type === 'photo') {
        return !!additionalServiceAttributePhotos[key] || !!additionalServiceAttributeValues[key];
      }
      const value = additionalServiceAttributeValues[key];
      return value !== '' && value !== null && value !== undefined;
    });
  })
);

const selectedCategoryIdStr = computed<string | null>({
  get: () => formData.categoryId ? String(formData.categoryId) : null,
  set: (id: string | null) => {
    const newCatId = id ? parseInt(String(id)) : null;
    if (newCatId !== formData.categoryId) {
      formData.categoryId = newCatId;
      formData.planId = null;
      formData.selectedPlan = null;
      formData.selectedAdditionalServices = [];
      formData.attributeValues = {};
      loadCategoryDependencies(newCatId);
    }
  }
});

// Pick a legible text color (near-black or white) for text laid over `hex`,
// using relative luminance — service colors are arbitrary, so a fixed white/black
// would be unreadable on the opposite end. Returns the default text var when no bg.
const contrastTextFor = (hex: string | null): string => {
  if (!hex) return 'var(--app-text-color)';
  const c = hex.replace('#', '');
  const full = c.length === 3 ? c.split('').map(ch => ch + ch).join('') : c;
  if (full.length < 6) return 'var(--app-text-color)';
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#1a1a1a' : '#ffffff';
};

const filteredServicePriorityOptions = computed(() =>
  servicePriorities.value
    .filter(sp => String(sp.so_service_category_id) === String(formData.categoryId))
    .slice()
    .sort((a, b) => Number(a.so_service_priority_order ?? 0) - Number(b.so_service_priority_order ?? 0))
    .map(sp => ({
      ID: String(sp.so_service_priority_id),
      Name: `${sp.so_service_name} — ${sp.so_priority_name}`,
      description: `${n(sp.so_service_priority_price ?? 0, 'currency')} · ${sp.so_service_priority_deadline_days} ${t('dias úteis')}`,
      serviceColor: sp.so_service_color || null,
      priorityColor: sp.so_priority_color || null,
      textColor: contrastTextFor(sp.so_service_color || null)
    }))
);

const selectedPlanId = computed<string | null>({
  get: () => formData.planId ? String(formData.planId) : null,
  set: (id: string | null) => {
    const plan = servicePriorities.value.find((sp: ServicePriority) => String(sp.so_service_priority_id) === id);
    formData.planId = plan ? (plan.so_service_priority_id ?? null) : null;
    formData.selectedPlan = plan || null;
  }
});

const additionalServicesOptions = computed(() =>
  additionalServicesList.value.map(svc => ({
    ID: String(svc.so_additional_service_id),
    Name: svc.so_additional_service_name,
    description: `${n(svc.so_additional_service_default_price ?? 0, 'currency')}${svc.so_additional_service_additional_deadline_days ? ` · +${svc.so_additional_service_additional_deadline_days} ${t('dias')}` : ''}`
  }))
);

const selectedAdditionalServiceIds = computed<string[]>({
  get: () => formData.selectedAdditionalServices.map((s: AdditionalService) => String(s.so_additional_service_id)),
  set: (ids: string[]) => {
    formData.selectedAdditionalServices = additionalServicesList.value.filter(
      (svc: AdditionalService) => ids.includes(String(svc.so_additional_service_id))
    );
  }
});

const comprasDoPlano = computed<CompraCombo[]>(() => {
  const plano = formData.selectedPlan;
  if (!plano || !franchiseId.value) return [];
  return comprasCompativeis(comprasDoCliente.value, {
    serviceId: Number(plano.so_service_priority_service_id ?? plano.service_id),
    priorityId: Number(plano.so_service_priority_priority_id ?? plano.priority_id),
    franchiseId: franchiseId.value,
    compraDoItem: comboOriginalId.value,
  });
});

// A compra do item que não veio na consulta do cliente (consulta falhou, compra
// fora do alcance): o vínculo existe e continua na tela, só sem nome e saldo.
const compraDoItemForaDaLista = computed<boolean>(() =>
  !!comboOriginalId.value && !comprasDoCliente.value.some((c) => c.compra_id === comboOriginalId.value));

const comboOptions = computed(() => {
  const opcoes = comprasDoPlano.value.map((c) => ({
    ID: c.compra_id,
    Name: `${c.combo_nome} · ${c.disponivel} ${t('disponíveis')}`,
  }));
  if (compraDoItemForaDaLista.value) {
    opcoes.unshift({ ID: Number(comboOriginalId.value), Name: t('Combo já vinculado ao item') });
  }
  return opcoes.length > 0 ? [{ ID: 0, Name: t('Não usar combo') }, ...opcoes] : [];
});

const comboSelectionId = computed<number>({
  get: () => comboPurchaseId.value ?? 0,
  set: (id) => {
    comboPurchaseId.value = Number(id) || null;
  },
});

const comboSelecionadoNome = computed<string>(() =>
  comprasDoCliente.value.find((c) => c.compra_id === comboPurchaseId.value)?.combo_nome
    ?? t('Combo já vinculado ao item'));

// Trocou para um plano que a compra escolhida não cobre: o par volta a ser pago.
// Compra que não está na consulta não prova nada: limpar aqui viraria um
// desvincular que ninguém pediu ao salvar a edição.
watch(comprasDoPlano, (compras) => {
  const id = comboPurchaseId.value;
  if (!formData.selectedPlan || !id || compras.some((c) => c.compra_id === id)) return;
  if (comprasDoCliente.value.some((c) => c.compra_id === id)) {
    comboPurchaseId.value = null;
  }
});

const toggleAdditionalService = (id: string): void => {
  const current = selectedAdditionalServiceIds.value;
  selectedAdditionalServiceIds.value = current.includes(id)
    ? current.filter(i => i !== id)
    : [...current, id];
};

const additionalServicesTotal = computed<number>(() => {
  return formData.selectedAdditionalServices.reduce((sum: number, svc: AdditionalService) => sum + (svc.so_additional_service_default_price || 0), 0);
});

const productsTotal = computed<number>(() => {
  let total = 0;
  for (const [prodId, qty] of Object.entries(formData.selectedProducts)) {
    if (qty > 0) {
      const prod = productsList.value.find((p: ProductItem) => String(p.so_product_id) === String(prodId));
      if (prod) total += (prod.so_product_default_price || 0) * qty;
    }
  }
  return total;
});

// Par coberto por combo sai a 0; adicionais e produtos continuam pagos.
const planPrice = computed<number>(() =>
  comboPurchaseId.value ? 0 : (formData.selectedPlan?.so_service_priority_price || 0));

const itemTotal = computed<number>(() => {
  return planPrice.value + additionalServicesTotal.value + productsTotal.value;
});

const estimatedDays = computed<number>(() => {
  const planDays = parseInt(String(formData.selectedPlan?.so_service_priority_deadline_days ?? 0)) || 0;
  const additionalDays = formData.selectedAdditionalServices.reduce(
    (sum: number, svc: AdditionalService) => sum + (parseInt(String(svc.so_additional_service_additional_deadline_days ?? 0)) || 0), 0
  );
  return planDays + additionalDays;
});

const resetForm = (): void => {
  currentWizardStep.value = 0;
  editItemId.value = null;
  formData.categoryId = null;
  formData.qrcode = '';
  formData.attributeValues = {};
  formData.planId = null;
  formData.selectedPlan = null;
  formData.selectedAdditionalServices = [];
  formData.selectedProducts = {};
  formData.requests = [];
  categoryAttributes.value = [];
  additionalServiceAttributes.value = {};
  comprasDoCliente.value = [];
  comboPurchaseId.value = null;
  comboOriginalId.value = null;
  Object.keys(additionalServiceAttributeValues).forEach((k) => delete additionalServiceAttributeValues[k]);
  Object.keys(additionalServiceAttributePhotos).forEach((k) => delete additionalServiceAttributePhotos[k]);
  qrCheck.reset();
};

const handleClose = (): void => {
  isVisible.value = false;
  resetForm();
};

const buildAttributesPayload = (): { attribute_id: number; value: string }[] => {
  return Object.entries(formData.attributeValues)
    .filter(([, value]) => value !== '' && value != null)
    .map(([attributeId, value]) => ({ attribute_id: parseInt(String(attributeId)), value: String(value) }));
};

// Non-photo attribute values for one additional service — these go on add_item.
// Photo values are deferred to phase 2 (upload after the child order_item_id exists).
const buildAdditionalServiceNonPhotoAttributes = (serviceId: Id): { attribute_id: number; value: string }[] => {
  return attributesForService(serviceId)
    .filter((attr) => attr.so_item_attribute_type !== 'photo')
    .map((attr) => ({
      attribute_id: Number(attr.so_asal_attribute_id),
      value: getAdditionalServiceAttrValue(serviceId, attr.so_asal_attribute_id)
    }))
    .filter((entry) => entry.value !== '' && entry.value != null);
};

// KNOWN GAP (edit path): on EDIT, this only re-sends additional_service IDs +
// category attributes — it does NOT replay per-additional-service attribute values,
// per-AS photos, or customer-request edits that the CREATE path persists, and it
// sends service_priority_id (a priority id) rather than CREATE's service_id +
// priority_id pair. Reconciling requires confirming the update_item backend contract.
const handleFinish = async (): Promise<void> => {
  log('PDVServicoWizardModal', 'handleFinish', 'Form data:', {
    orderId: orderId.value,
    editItemId: editItemId.value,
    qrcode: formData.qrcode,
    attributeValues: formData.attributeValues,
    selectedPlan: formData.selectedPlan?.so_service_name,
    selectedPlanId: formData.planId,
    selectedAdditionalServices: formData.selectedAdditionalServices.length,
    selectedProducts: formData.selectedProducts
  });

  const attributes = buildAttributesPayload();

  try {
    if (isEditMode.value) {
      // UPDATE mode
      const updatePayload = {
        qrcode: formData.qrcode || undefined,
        service_priority_id: formData.selectedPlan?.so_service_priority_priority_id !== undefined ? Number(formData.selectedPlan.so_service_priority_priority_id) : undefined,
        additional_services: formData.selectedAdditionalServices.map((s: AdditionalService) => s.so_additional_service_id).filter((id): id is number => id !== undefined),
        products: Object.entries(formData.selectedProducts)
          .filter(([, qty]) => qty > 0)
          .map(([prodId, qty]) => ({ product_id: parseInt(String(prodId)), quantity: qty })),
        attributes
      };
      log('PDVServicoWizardModal', 'handleFinish', 'Update payload:', JSON.stringify(updatePayload));
      if (editItemId.value) {
        // Desvincular ANTES do update: item coberto recusa troca de prioridade.
        // Vincular DEPOIS: o combo confere o serviço e a prioridade já gravados.
        const trocouCombo = comboPurchaseId.value !== comboOriginalId.value;
        if (trocouCombo && comboOriginalId.value) {
          await desvincular(Number(editItemId.value));
          // Já saiu: se o update recusar, a nova tentativa não desvincula de novo.
          comboOriginalId.value = null;
        }
        await updateItem(editItemId.value, updatePayload);
        if (trocouCombo && comboPurchaseId.value) {
          await vincular(Number(editItemId.value), { combo_purchase_id: comboPurchaseId.value });
        }
      }
      eventBus.emit('globalMessage.success', t('Serviço atualizado'));
    } else {
      // CREATE mode
      // 1. Add service item (the plan)
      if (!formData.selectedPlan || !orderId.value) {
        throw new Error(t('Plano de serviço não selecionado'));
      }
      const servicePayload = {
        type: 'service',
        service_id: formData.selectedPlan.so_service_priority_service_id || formData.selectedPlan.service_id,
        priority_id: formData.selectedPlan.so_service_priority_priority_id || formData.selectedPlan.priority_id,
        qrcode: formData.qrcode || null,
        attributes,
        ...(comboPurchaseId.value ? { combo_purchase_id: comboPurchaseId.value } : {})
      };
      log('PDVServicoWizardModal', 'handleFinish', 'Service payload:', JSON.stringify(servicePayload));
      const serviceResult = await addItem(orderId.value, servicePayload) as { dados?: { item_id?: Id; so_order_item_id?: Id } };
      log('PDVServicoWizardModal', 'handleFinish', 'Service result:', JSON.stringify(serviceResult));

      const parentItemId = serviceResult.dados?.item_id || serviceResult.dados?.so_order_item_id;

      // 2. Add additional services as child items (with their non-photo attribute
      //    values), then upload + submit any photo attributes in a second phase.
      for (const svc of formData.selectedAdditionalServices) {
        if (!orderId.value || svc.so_additional_service_id === undefined) continue;
        const serviceId = svc.so_additional_service_id;
        const nonPhotoAttributes = buildAdditionalServiceNonPhotoAttributes(serviceId);
        // Phase 1: create the child row with the non-photo values. BE does NOT reject on
        // a still-missing required photo here (required-enforcement is deferred).
        const childResult = await addItem(orderId.value, {
          type: 'additional_service',
          additional_service_id: serviceId,
          parent_item_id: parentItemId,
          attributes: nonPhotoAttributes
        }) as { dados?: { item_id?: Id; so_order_item_id?: Id } };
        const childItemId = childResult.dados?.item_id || childResult.dados?.so_order_item_id;

        // Phase 2: now that the child order_item_id exists, upload each photo attribute
        // and submit its returned URL via update_item, where BE runs the required-check.
        const photoAttrs = attributesForService(serviceId).filter((attr) => attr.so_item_attribute_type === 'photo');
        if (childItemId && photoAttrs.length > 0) {
          const photoValues: { attribute_id: number; value: string }[] = [];
          for (const attr of photoAttrs) {
            const file = additionalServiceAttributePhotos[asAttrKey(serviceId, attr.so_asal_attribute_id)];
            if (!file) continue;
            const { url } = await uploadAttributePhoto(file, childItemId, attr.so_asal_attribute_id);
            photoValues.push({ attribute_id: Number(attr.so_asal_attribute_id), value: url });
          }
          if (photoValues.length > 0) {
            await updateItem(childItemId, { attributes: [...nonPhotoAttributes, ...photoValues] });
          }
        }
      }

      // 3. Add products as child items
      for (const [prodId, qty] of Object.entries(formData.selectedProducts)) {
        if (qty > 0 && orderId.value) {
          await addItem(orderId.value, {
            type: 'product',
            product_id: parseInt(String(prodId)),
            quantity: qty,
            parent_item_id: parentItemId
          });
        }
      }

      // 4. Upload photos and save customer requests
      if (formData.requests.length > 0) {
        const uploaded = await Promise.all(
          formData.requests.map(async (req: RequestItem) => {
            let photo: string | null = req.photo || null;
            if (req.photo_file) {
              photo = await uploadPhoto(req.photo_file) as string;
            }
            return {
              description: req.description,
              photo,
              display_order: req.display_order,
            };
          })
        );
        await saveAll(parentItemId, uploaded);
      }

      eventBus.emit('globalMessage.success', t('Serviço adicionado à ordem'));
    }

    eventBus.emit('pdv.item-added');
    handleClose();
  } catch (err) {
    logError('PDVServicoWizardModal', 'handleFinish', 'Error', err);
    const message = err instanceof Error ? err.message : (err as { message?: string })?.message;
    eventBus.emit('globalMessage.error', message || t('Erro ao salvar serviço'));
  }
};

// Fetch attribute bindings for each selected additional service that hasn't been
// loaded yet, so the inline sub-panel can render its inputs. Errors surface as a toast.
const loadAdditionalServiceAttributes = async (serviceIds: Id[]): Promise<void> => {
  const missing = serviceIds.filter((id) => additionalServiceAttributes.value[String(id)] === undefined);
  if (missing.length === 0) return;
  try {
    await Promise.all(
      missing.map(async (id) => {
        const bindings = await fetchAdditionalServiceAttrs(Number(id)) as unknown as AdditionalServiceAttributeBinding[];
        additionalServiceAttributes.value = {
          ...additionalServiceAttributes.value,
          [String(id)]: bindings
        };
      })
    );
    log('PDVServicoWizardModal', 'loadAdditionalServiceAttributes', 'Loaded bindings', { serviceIds: missing });
  } catch (err) {
    logError('PDVServicoWizardModal', 'loadAdditionalServiceAttributes', 'Error', err);
    const msg = (err as Error | undefined)?.message ?? '';
    eventBus.emit('globalMessage.error', t('Falha ao carregar atributos do serviço adicional') + (msg ? ': ' + msg : ''));
  }
};

const loadCategoryDependencies = async (categoryId: number | null): Promise<void> => {
  if (!categoryId) {
    additionalServicesList.value = [];
    categoryAttributes.value = [];
    return;
  }
  try {
    const [additionalSvcs, attrs] = await Promise.all([
      // A franquia da OS resolve o preço/prazo POR FRANQUIA dos adicionais
      // (migração E3) e esconde os que ela desligou; sem franquia ainda
      // resolvida, os defaults globais — como sempre foi.
      fetchAdditionalServicesByCategory(categoryId, franchiseId.value ?? undefined),
      fetchCategoryAttrs(categoryId)
    ]) as unknown as [AdditionalService[], CategoryAttribute[]];
    additionalServicesList.value = additionalSvcs;
    categoryAttributes.value = attrs;
    log('PDVServicoWizardModal', 'loadCategoryDependencies', 'Loaded', {
      additionalServices: additionalSvcs.length,
      attributes: attrs.length
    });
  } catch (err) {
    logError('PDVServicoWizardModal', 'loadCategoryDependencies', 'Error', err);
    additionalServicesList.value = [];
    categoryAttributes.value = [];
    // Scream loud: a category-load failure means the wizard can't proceed
    // (no attribute steps will render). Surface a toast so the user / test
    // sees the error, instead of silently degrading to an empty wizard.
    const msg = (err as Error | undefined)?.message ?? t('Falha ao carregar dados da categoria');
    eventBus.emit('globalMessage.error', t('Falha ao carregar atributos da categoria') + ': ' + msg);
  }
};

// Products catalog can be large (1000+ rows) and is only consumed several
// steps after category selection, so it is fetched in the background and must
// never block the wizard from rendering its first (category) step.
const loadProductsInBackground = (): void => {
  productsCatalog.fetchAll()
    .then((products) => {
      productsList.value = products as ProductItem[];
      log('PDVServicoWizardModal', 'loadCatalogs', 'Loaded products:', productsList.value.length);
    })
    .catch((err) => {
      logError('PDVServicoWizardModal', 'loadCatalogs', 'Products load failed', err);
      productsList.value = [];
    });
};

const loadCatalogs = async (): Promise<void> => {
  catalogsLoading.value = true;
  try {
    if (!franchiseId.value && orderId.value) {
      const order = await getOrder(orderId.value) as { so_order_franchise_id?: Id } | undefined;
      franchiseId.value = Number(order?.so_order_franchise_id || 0) || null;
    }

    if (!franchiseId.value) {
      servicePriorities.value = [];
      loadProductsInBackground();
      eventBus.emit('globalMessage.error', t('Nao foi possivel identificar a franquia da ordem'));
      return;
    }

    // Categories/plans (with their SVG icons) come from service_priorities and
    // are all the first wizard step needs — await only this so the category grid
    // renders immediately instead of blocking ~11s on the full products catalog.
    servicePriorities.value = await fetchServicePrioritiesByFranchise(franchiseId.value) as ServicePriority[];
    loadProductsInBackground();
    log('PDVServicoWizardModal', 'loadCatalogs', 'Loaded categories:', categoryOptions.value.length);
  } catch (err) {
    logError('PDVServicoWizardModal', 'loadCatalogs', 'Error', err);
    servicePriorities.value = [];
    productsList.value = [];
  } finally {
    catalogsLoading.value = false;
  }
};

// As compras do cliente da OS. Falhar aqui não trava o wizard: o serviço continua
// vendável pelo preço da matriz.
const loadCombosDoCliente = async (): Promise<void> => {
  if (!combosEnabled || !canUseCombos.value || !orderId.value) return;
  try {
    const order = await getOrder(orderId.value) as { so_order_client_id?: Id } | undefined;
    const clienteId = Number(order?.so_order_client_id || 0);
    if (!clienteId) return;
    comprasDoCliente.value = await fetchCompras(clienteId);
  } catch (err) {
    logError('PDVServicoWizardModal', 'loadCombosDoCliente', 'Error', err);
    const message = err instanceof Error ? err.message : '';
    eventBus.emit('globalMessage.error', t('Não foi possível carregar os combos do cliente') + ': ' + message);
  }
};

const handleModalOpen = async (data?: OpenPayload): Promise<void> => {
  if (!data) return;
  log('PDVServicoWizardModal', 'handleModalOpen', 'Opening', data);
  isVisible.value = true;
  resetForm();
  orderId.value = data.orderId ?? null;
  franchiseId.value = Number(data.franchiseId || 0) || null;
  editItemId.value = data.editItem?.so_order_item_id || null;
  comboOriginalId.value = data.editItem?.so_order_item_combo_purchase_id ?? null;
  comboPurchaseId.value = comboOriginalId.value;
  await loadCatalogs();
  await loadCombosDoCliente();

  // Fresh open: auto-select the first category so its dependencies and
  // additional-services load without an extra click (mirrors the legacy
  // buttons-grid autoSelectFirstValue behaviour the wizard had before the
  // premium card redesign). Skipped in edit mode — the category comes from
  // the saved item below.
  if (!data.editItem && !formData.categoryId && categoryOptions.value.length > 0) {
    selectedCategoryIdStr.value = categoryOptions.value[0].ID;
  }

  // Populate form if editing
  if (data.editItem) {
    const item = data.editItem;
    log('PDVServicoWizardModal', 'handleModalOpen', 'Edit item data:', item);

    formData.qrcode = item.so_order_item_qrcode || '';

    // Populate attribute values from saved data
    if (item.attributes && item.attributes.length > 0) {
      const attrMap: Record<string, string> = {};
      for (const av of item.attributes) {
        if (av.so_oiav_attribute_id !== undefined) {
          attrMap[String(av.so_oiav_attribute_id)] = String(av.so_oiav_value ?? '');
        }
      }
      formData.attributeValues = attrMap;
    }

    // Find the plan by service_id and priority_id, and pre-select category
    if (item.so_order_item_service_id && item.so_order_item_priority_id) {
      const matchingPlan = servicePriorities.value.find(
        (sp: ServicePriority) => sp.so_service_priority_service_id === item.so_order_item_service_id &&
              sp.so_service_priority_priority_id === item.so_order_item_priority_id
      );

      log('PDVServicoWizardModal', 'handleModalOpen', 'Looking for plan:', {
        service_id: item.so_order_item_service_id,
        priority_id: item.so_order_item_priority_id,
        found: !!matchingPlan,
        availablePlans: servicePriorities.value.length
      });

      if (matchingPlan) {
        // Pre-select category from the plan (without resetting form fields)
        formData.categoryId = parseInt(String(matchingPlan.so_service_category_id ?? 0));
        await loadCategoryDependencies(formData.categoryId);
        // Use the computed setter by assigning to selectedPlanId
        selectedPlanId.value = String(matchingPlan.so_service_priority_id);
      }
    }

    // Populate children (additional services and products)
    if (data.children && data.children.length > 0) {
      const additionalServices = data.children.filter((c: ChildItem) => c.so_order_item_type === 'additional_service');
      const products = data.children.filter((c: ChildItem) => c.so_order_item_type === 'product');

      // Map children to catalog items for the form
      formData.selectedAdditionalServices = additionalServices.map((c: ChildItem): AdditionalService => {
        const catalogItem = additionalServicesList.value.find(
          (svc: AdditionalService) => svc.so_additional_service_id === c.so_order_item_additional_service_id
        );
        return catalogItem || {
          so_additional_service_id: c.so_order_item_additional_service_id,
          so_additional_service_name: c.so_order_item_description,
          so_additional_service_default_price: c.so_order_item_unit_price
        };
      });

      const productsMap: Record<string, number> = {};
      for (const p of products) {
        if (p.so_order_item_product_id !== undefined) {
          productsMap[String(p.so_order_item_product_id)] = p.so_order_item_quantity || 1;
        }
      }
      formData.selectedProducts = productsMap;
    }

    log('PDVServicoWizardModal', 'handleModalOpen', 'Edit mode populated', {
      qrcode: formData.qrcode,
      attributeValues: formData.attributeValues,
      planId: formData.planId,
      selectedPlan: formData.selectedPlan?.so_service_name,
      additionalServices: formData.selectedAdditionalServices.length,
      products: Object.keys(formData.selectedProducts).length
    });
  }
};

// When the set of selected additional services changes, lazily load each one's
// attribute bindings so the inline sub-panel renders the required inputs.
watch(
  () => formData.selectedAdditionalServices.map((s) => s.so_additional_service_id),
  (ids) => {
    const known = ids.filter((id): id is Id => id !== undefined);
    if (known.length > 0) loadAdditionalServiceAttributes(known);
  }
);

onMounted(() => {
  eventBus.on('modal.pdv-servico-wizard.open', handleModalOpen);
});

onBeforeUnmount(() => {
  eventBus.off('modal.pdv-servico-wizard.open', handleModalOpen);
});
</script>

<style scoped>
/* ── Step Headers ── */
.step-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--app-text-color);
  margin: 0 0 4px;
}

.step-desc {
  font-size: 0.875rem;
  color: var(--app-text-secondary-color);
  margin: 0 0 20px;
}

/* ── Usar combo do cliente ── */
.combo-selector {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 16px;
  max-width: 480px;
}

.combo-hint {
  font-size: 0.8rem;
  color: var(--p-text-muted-color);
}

/* ── QR Code Input ── */
.qrcode-input-wrapper {
  max-width: 50%;
}

@media (max-width: 768px) {
  .qrcode-input-wrapper {
    max-width: 100%;
  }
}

/* ── Loading ── */
.loading-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 40px;
  color: var(--p-text-muted-color);
  font-size: 0.9rem;
}

.loading-indicator i {
  color: var(--app-primary-color);
}

/* ── QR Code live uniqueness status ── */
.qrcode-check-status {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  font-size: 0.9rem;
}

.qrcode-check-checking {
  color: var(--p-text-muted-color);
}

.qrcode-check-checking i {
  color: var(--app-primary-color);
}

.qrcode-check-unique {
  color: var(--p-green-500);
}

.qrcode-check-duplicate,
.qrcode-check-error {
  color: var(--p-red-500);
}

/* ── Step Input Area ── */
.step-input-area {
  max-width: 480px;
}

/* ── Selection Grid ── */
.selection-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(240px, 300px));
  gap: 10px;
  justify-content: start;
  align-content: start;
}

.selection-grid-compact {
  grid-template-columns: repeat(2, minmax(200px, 260px));
}

/* ── Selection Card ── */
.selection-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 72px;
  padding: 14px 16px;
  background: var(--p-surface-0);
  border: 1.5px solid var(--p-surface-200);
  border-radius: 12px;
  cursor: pointer;
  transition: all 150ms ease;
  user-select: none;
}

.selection-card:hover {
  border-color: var(--p-surface-300);
  background: var(--p-surface-50);
}

/* Plan cards: background = service color, border = priority color.
   Text switches to a luminance-contrasted color (--plan-text) so the
   title/subtitle stay legible over arbitrary service-color backgrounds. */
.selection-card.plan-card--colored {
  /* Hover/active/selected darken the service-color background with a translucent
     black overlay (instead of the default gray/primary fills, which would erase
     --plan-bg). Strength is tunable via these vars. */
  --plan-overlay-hover: rgba(0, 0, 0, 0.06);
  --plan-overlay-active: rgba(0, 0, 0, 0.14);
  --plan-overlay-selected: rgba(0, 0, 0, 0.09);
  background: var(--plan-bg);
  border: 1.5px solid var(--plan-border);
}

.selection-card.plan-card--colored .selection-title,
.selection-card.plan-card--colored .selection-subtitle,
.selection-card.plan-card--colored .selection-chevron {
  color: var(--plan-text, var(--app-text-color));
}

/* Colored-plan interaction states: overlay-over-bg, not gray. The equal-stop
   linear-gradient is a flat tint layered above the service color; `all 150ms`
   on .selection-card gives the fade. Border stays the priority color. */
.selection-card.plan-card--colored:hover {
  background: linear-gradient(var(--plan-overlay-hover), var(--plan-overlay-hover)), var(--plan-bg);
  border-color: var(--plan-border);
}

.selection-card.plan-card--colored:active {
  background: linear-gradient(var(--plan-overlay-active), var(--plan-overlay-active)), var(--plan-bg);
}

.selection-card.plan-card--colored.selection-card--selected,
.selection-card.plan-card--colored.selection-card--selected:hover {
  background: linear-gradient(var(--plan-overlay-selected), var(--plan-overlay-selected)), var(--plan-bg);
  border-color: var(--plan-border);
}

.selection-card:active {
  background: var(--p-surface-100);
}

.selection-card:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(var(--app-primary-color-rgb), 0.12);
}

.selection-card--selected {
  border-color: var(--app-primary-color);
  background: rgba(var(--app-primary-color-rgb), 0.03);
}

.selection-card--selected:hover {
  background: rgba(var(--app-primary-color-rgb), 0.06);
}

/* ── Selection Left Area ── */
.selection-left {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
  flex: 1;
}

/* ── Category Icon ── */
.category-icon {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.category-icon :deep(svg) {
  width: 100%;
  height: 100%;
  fill: currentColor;
}

/* ── Radio Indicator ── */
.selection-indicator {
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 2px solid var(--p-surface-300);
  margin-top: 1px;
  flex-shrink: 0;
  transition: all 150ms ease;
}

.selection-indicator--active {
  border-color: var(--app-primary-color);
  background: var(--app-primary-color);
  box-shadow: inset 0 0 0 4px var(--p-surface-0);
}

/* ── Checkbox Indicator (multi-select) ── */
.selection-checkbox {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 2px solid var(--p-surface-300);
  margin-top: 1px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 150ms ease;
}

.selection-checkbox--active {
  border-color: var(--app-primary-color);
  background: var(--app-primary-color);
}

.selection-checkbox--active i {
  color: var(--p-surface-0);
  font-size: 11px;
}

/* ── Card Content ── */
.selection-content {
  min-width: 0;
}

.selection-title {
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.3;
  color: var(--app-text-color);
}

.selection-subtitle {
  font-size: 0.8rem;
  font-weight: 400;
  color: var(--app-text-secondary-color);
  margin-top: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Chevron ── */
.selection-chevron {
  color: var(--p-text-muted-color);
  font-size: 0.7rem;
  flex-shrink: 0;
  margin-left: 8px;
}

.selection-card--selected .selection-chevron {
  color: var(--app-primary-color);
}

/* ── Deadline Display ── */
.deadline-display {
  margin-top: 14px;
  padding: 10px 14px;
  background: var(--p-surface-50);
  border-radius: 8px;
  color: var(--app-text-secondary-color);
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 8px;
}

.deadline-display i {
  color: var(--app-primary-color);
}

/* ── Services ── */
.services-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.services-total {
  text-align: right;
  font-weight: 600;
  padding-top: 8px;
  color: var(--app-text-color);
}

/* ── Summary ── */
.item-summary {
  margin-top: 0;
}

.summary-section-title {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--app-text-secondary-color);
  margin: 20px 0 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--p-surface-200);
}

.summary-section-title:first-child {
  margin-top: 0;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 0;
  font-size: 0.875rem;
}

.summary-label {
  color: var(--app-text-secondary-color);
}

.summary-value {
  font-weight: 600;
  color: var(--app-text-color);
}

.summary-total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0 4px;
  margin-top: 8px;
  border-top: 2px solid var(--p-surface-200);
  font-weight: 700;
  font-size: 1.05rem;
}

.summary-total-row .summary-value {
  color: var(--app-primary-color);
  font-size: 1.15rem;
}

.summary-deadline-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  font-size: 0.85rem;
  color: var(--app-text-secondary-color);
}

.category-attributes {
  margin-top: 8px;
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .selection-grid {
    grid-template-columns: 1fr;
  }

  .selection-grid-compact {
    grid-template-columns: 1fr;
  }

  .step-title {
    font-size: 1.15rem;
  }
}

/* ── Category Search ── */
.selection-search {
  position: relative;
  margin-bottom: 16px;
}

.selection-search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--p-text-muted-color);
  font-size: 0.9rem;
  pointer-events: none;
}

.selection-search-input {
  width: 100%;
  height: 44px;
  padding: 0 14px 0 40px;
  border: 1.5px solid var(--p-surface-200);
  border-radius: 8px;
  font-size: 0.875rem;
  color: var(--app-text-color);
  background: var(--p-surface-0);
  outline: none;
  transition: all 150ms ease;
  box-sizing: border-box;
}

.selection-search-input::placeholder {
  color: var(--p-text-muted-color);
}

.selection-search-input:hover {
  border-color: var(--p-surface-300);
}

.selection-search-input:focus {
  border-color: var(--app-primary-color);
  box-shadow: 0 0 0 3px rgba(var(--app-primary-color-rgb), 0.08);
}

/* ── Products Step Wrapper ── */
.products-step-wrapper :deep(.so-product-selector) {
  gap: 12px;
}

.products-step-wrapper :deep(.so-product-selector > button) {
  border-radius: 8px;
  align-self: flex-start;
}

.products-step-wrapper :deep(.selected-products) {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.products-step-wrapper :deep(.selected-product-row) {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: var(--p-surface-0);
  border: 1.5px solid var(--p-surface-200);
  border-radius: 8px;
  transition: all 150ms ease;
}

.products-step-wrapper :deep(.selected-product-row:hover) {
  border-color: var(--p-surface-300);
}

.products-step-wrapper :deep(.selected-product-row .product-name) {
  flex: 1;
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--app-text-color);
}

.products-step-wrapper :deep(.selected-product-row .product-price) {
  color: var(--app-text-secondary-color);
  min-width: 70px;
  text-align: right;
  font-size: 0.85rem;
}

.products-step-wrapper :deep(.selected-product-row .product-subtotal) {
  font-weight: 700;
  min-width: 90px;
  text-align: right;
  color: var(--app-primary-color);
}

.products-step-wrapper :deep(.qty-controls) {
  display: flex;
  align-items: center;
  gap: 4px;
  background: var(--p-surface-50);
  border-radius: 8px;
  padding: 2px 4px;
}

.products-step-wrapper :deep(.qty-controls .qty-value) {
  min-width: 24px;
  text-align: center;
  font-weight: 700;
  font-size: 0.85rem;
}

.products-step-wrapper :deep(.products-total) {
  text-align: right;
  font-weight: 700;
  font-size: 0.95rem;
  padding-top: 12px;
  margin-top: 8px;
  border-top: 2px solid var(--p-surface-200);
  color: var(--app-text-color);
}

.products-step-wrapper :deep(.no-products) {
  text-align: center;
  padding: 32px 20px;
  color: var(--p-text-muted-color);
  background: var(--p-surface-50);
  border-radius: 8px;
  border: 1.5px dashed var(--p-surface-200);
  font-size: 0.875rem;
}

/* ── Additional-Service Attribute Sub-Panel ── */
.additional-service-attr-panel {
  margin-top: 12px;
  padding: 12px;
  border: 1px solid var(--p-surface-200);
  border-radius: 8px;
}

.additional-service-attr-panel h5 {
  margin: 0 0 8px;
}

.additional-service-attr-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 10px;
}

.additional-service-photo {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.additional-service-photo label {
  font-weight: 600;
}

.additional-service-photo .required-indicator {
  color: var(--p-red-500);
  margin-right: 4px;
}

.additional-service-photo .photo-preview {
  max-width: 150px;
}

.additional-service-photo .preview-image {
  max-width: 150px;
  border-radius: 6px;
}
</style>
